/**
 * Super-admin pipeline-run read API (Phase 2a backend, Phase 2b UI).
 *
 * Backs:
 *   GET /api/admin/pipeline-runs?limit&offset
 *   GET /api/admin/pipeline-runs/:runId
 *
 * Cross-tenant run data — platform super-admins only. Uses the raw Eden
 * `api` client (NOT company-scoped). Types mirror the Elysia schemas in
 * `backend/src/modules/admin/pipeline-runs/model.ts`.
 */

import type { IPipelineGraphEdge, IPipelineGraphNode } from "../../components/PipelineGraph/interfaces"
import { api } from "./client.ts"
import { unwrap, unwrapData } from "./errors.ts"

/** Run-level status (header). Distinct from per-step `TStepStatus`. */
export type TPipelineRunStatus = "running" | "complete" | "failed"

/** Per-step runtime status — same union as `TStepStatus` on PipelineGraph. */
export type TPipelineStepStatus = "queued" | "running" | "done" | "failed" | "skipped"

export interface IPipelineRunSlowestStep {
  nodeId: string
  durationMs: number
}

/** List-row summary from GET /api/admin/pipeline-runs. */
export interface IPipelineRunSummary {
  id: string
  meetingId: string
  companyId: string
  template: string
  status: TPipelineRunStatus
  podId: string | null
  startedAt: string
  finishedAt: string | null
  /** Organization display name. Null when the company row is gone. */
  companyName: string | null
  /** Meeting start, or created time when start is blank. ISO, or null. */
  meetingStartedAt: string | null
  /** Whole minutes. Null when unknown (missing, zero, or not a number). */
  meetingDurationMinutes: number | null
  totalDurationMs: number | null
  stepCount: number
  slowestStep: IPipelineRunSlowestStep | null
  failedStepCount: number
}

/** Run header on the detail payload. */
export interface IPipelineRun {
  id: string
  meetingId: string
  companyId: string
  template: string
  status: TPipelineRunStatus
  podId: string | null
  startedAt: string
  finishedAt: string | null
  /** Organization display name. Null when the company row is gone. */
  companyName: string | null
  /** Meeting start, or created time when start is blank. ISO, or null. */
  meetingStartedAt: string | null
  /** Whole minutes. Null when unknown (missing, zero, or not a number). */
  meetingDurationMinutes: number | null
}

/** Latest-per-node step row from GET /api/admin/pipeline-runs/:runId. */
export interface IPipelineRunStep {
  nodeId: string
  wave: number
  status: TPipelineStepStatus
  durationMs: number | null
  error: string | null
  startedAt: string | null
  finishedAt: string | null
}

export interface IPipelineRunGraph {
  nodes: IPipelineGraphNode[]
  edges: IPipelineGraphEdge[]
  waveCount: number
}

export interface IPipelineRunDetail {
  run: IPipelineRun
  steps: IPipelineRunStep[]
  graph: IPipelineRunGraph | null
}

export interface IPipelineRunsPage {
  rows: IPipelineRunSummary[]
  total: number
  page: number
  pageSize: number
}

/** Paginated recent pipeline runs (super-admin, cross-tenant). */
export async function fetchPipelineRuns(limit = 50, offset = 0): Promise<IPipelineRunsPage> {
  const body = unwrap(
    await api.api.admin["pipeline-runs"].get({
      query: { limit, offset },
    }),
  )
  return {
    rows: body.data,
    total: body.meta.total,
    page: body.meta.page,
    pageSize: body.meta.pageSize,
  }
}

/** One run: header + latest steps + template topology (graph may be null). */
export async function fetchPipelineRunDetail(runId: string): Promise<IPipelineRunDetail> {
  return unwrapData(await api.api.admin["pipeline-runs"]({ runId }).get())
}
